import {
  createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect,
} from '@tanstack/react-router';
import { authClient } from '@/lib/auth-client';
import { Shell } from '@/routes/shell';

/**
 * Ten routes, defined in code rather than by file convention — at this size a generated
 * route tree is a build step that hides a list this short.
 *
 * Each screen is loaded on demand. The shell is not: it renders on every route, so
 * deferring it would only buy a flash of empty page. `defaultPreload: 'intent'` fetches a
 * screen's chunk when the pointer touches its link, which in practice means the chunk has
 * arrived before the click resolves.
 */

const lazy = <T extends string>(
  load: () => Promise<Record<string, unknown>>,
  exportName: T,
) => lazyRouteComponent(async () => {
  const reloadKey = `moves:chunk-reload:${window.location.pathname}`;
  try {
    const module = await load();
    sessionStorage.removeItem(reloadKey);
    return module;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const isStaleChunk = /module script failed|dynamically imported module|importing a module/i.test(message);
    if (isStaleChunk && sessionStorage.getItem(reloadKey) !== '1') {
      sessionStorage.setItem(reloadKey, '1');
      window.location.reload();
      return new Promise<Record<string, unknown>>(() => undefined);
    }
    throw error;
  }
}, exportName);

const rootRoute = createRootRoute({ component: Shell });

/** Everything except sign-in needs a session; ask the API once and let the router wait. */
async function requireSession({ location }: { location: { href: string } }) {
  const { data } = await authClient.getSession();
  if (!data?.user) {
    throw redirect({ to: '/sign-in', search: { next: location.href } });
  }
}

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: lazy(() => import('@/routes/sign-in'), 'SignIn'),
  // Optional rather than `string | undefined`: a required-but-undefined search param
  // would force every <Link to="/sign-in"> in the app to pass an empty object.
  validateSearch: (search: Record<string, unknown>): { next?: string } =>
    typeof search.next === 'string' ? { next: search.next } : {},
});

const thisWeekRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: requireSession,
  // `/` is This week on app.moves.watch and Operations on admin.moves.watch; see home.tsx.
  component: lazy(() => import('@/routes/home'), 'Home'),
});

const competitorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/competitors',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/competitors'), 'Competitors'),
});

const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/competitors/$slug',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/company'), 'CompanyPage'),
});

const directoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/directory',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/directory'), 'Directory'),
});

const marketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/directory/$market',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/market'), 'MarketPage'),
});

const youRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/you',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/you'), 'You'),
});

const battlecardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/battlecards',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/battlecards'), 'Battlecards'),
});

const battlecardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/battlecards/$slug',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/battlecard'), 'BattlecardPage'),
});

/**
 * Operations. Not in the nav: it is served at its own hostname (admin.moves.watch) and
 * the API answers it only for an admin-scoped account, so a link in the header would be a
 * dead end for every reader who is not the operator.
 */
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  beforeLoad: requireSession,
  component: lazy(() => import('@/routes/admin'), 'Admin'),
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  thisWeekRoute,
  competitorsRoute,
  companyRoute,
  directoryRoute,
  marketRoute,
  youRoute,
  battlecardsRoute,
  battlecardRoute,
  adminRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
